/**
 * m3 echo Frontend - Pricing Section for KI-Modelle Tab
 *
 * Manages versioned model pricing with effective_from dates.
 * Replaces the old ModelPricingDialog that lived in Kostenreports.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { settingsApi } from '../../services/api';

interface PricingEntry {
  model_name: string;
  input_price_per_million: number;
  output_price_per_million: number;
  pricing_unit: string;
  price_per_response: number;
  effective_from: string;
  last_verified_at: string | null;
}

interface PricingSectionProps {
  /** Wird nach erfolgreichem Speichern oder Bestätigen eines Preises aufgerufen. */
  onSaved?: () => void;
}

const PricingSection: React.FC<PricingSectionProps> = ({ onSaved }) => {
  const [pricing, setPricing] = useState<PricingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formModelName, setFormModelName] = useState('');
  const [formInputPrice, setFormInputPrice] = useState('');
  const [formOutputPrice, setFormOutputPrice] = useState('');
  const [formPricingUnit, setFormPricingUnit] = useState('per_million_tokens');
  const [formPricePerResponse, setFormPricePerResponse] = useState('');
  const [formEffectiveFrom, setFormEffectiveFrom] = useState(
    new Date().toISOString().split('T')[0]
  );

  const loadPricing = async () => {
    try {
      setLoading(true);
      const res = await settingsApi.getModelPricing();
      setPricing(res.data);
    } catch (e) {
      console.error('Failed to load pricing:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricing();
  }, []);

  // Group pricing by model name
  const groupedPricing = React.useMemo(() => {
    const groups: Record<string, PricingEntry[]> = {};
    for (const entry of pricing) {
      if (!groups[entry.model_name]) {
        groups[entry.model_name] = [];
      }
      groups[entry.model_name].push(entry);
    }
    // Sort each group by effective_from DESC
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => b.effective_from.localeCompare(a.effective_from));
    }
    return groups;
  }, [pricing]);

  const handleVerify = async (modelName: string) => {
    try {
      await settingsApi.verifyModelPricing(modelName);
      loadPricing();
      onSaved?.();
    } catch (e) {
      console.error('Failed to verify pricing:', e);
    }
  };

  const handleAddPrice = async () => {
    try {
      setError(null);
      const data: any = {
        model_name: formModelName,
        input_price_per_million: parseFloat(formInputPrice) || 0,
        output_price_per_million: parseFloat(formOutputPrice) || 0,
        pricing_unit: formPricingUnit,
        effective_from: formEffectiveFrom,
      };
      if (formPricingUnit === 'per_response') {
        data.price_per_response = parseFloat(formPricePerResponse) || 0;
      }
      await settingsApi.bulkUpdateModelPricing([data]);
      setDialogOpen(false);
      resetForm();
      loadPricing();
      onSaved?.();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const resetForm = () => {
    setFormModelName('');
    setFormInputPrice('');
    setFormOutputPrice('');
    setFormPricingUnit('per_million_tokens');
    setFormPricePerResponse('');
    setFormEffectiveFrom(new Date().toISOString().split('T')[0]);
  };

  const modelNames = Object.keys(groupedPricing).sort();
  const knownModels = [...new Set(pricing.map((p) => p.model_name))].sort();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Modellpreise</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Neuer Preis
        </Button>
      </Box>

      {loading ? (
        <Typography color="text.secondary">Lade Preise...</Typography>
      ) : modelNames.length === 0 ? (
        <Alert severity="info">Keine Modellpreise konfiguriert.</Alert>
      ) : (
        modelNames.map((modelName) => {
          const entries = groupedPricing[modelName];
          const latest = entries[0];
          const hasHistory = entries.length > 1;

          return (
            <Accordion key={modelName} disableGutters defaultExpanded={false}>
              <AccordionSummary expandIcon={hasHistory ? <ExpandMoreIcon /> : undefined}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 200 }}>
                    {modelName}
                  </Typography>
                  {latest.pricing_unit === 'per_response' ? (
                    <Typography variant="body2" color="text.secondary">
                      ${latest.price_per_response}/Aufruf
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      ${latest.input_price_per_million} / ${latest.output_price_per_million} pro 1M
                    </Typography>
                  )}
                  <Chip label={`ab ${latest.effective_from}`} size="small" variant="outlined" />
                  {latest.last_verified_at && (
                    <Chip
                      label={`Verifiziert ${new Date(latest.last_verified_at + 'Z').toLocaleDateString('de-DE')}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  <Box sx={{ ml: 'auto' }}>
                    <Tooltip title="Preis bestaetigt (Datum aktualisieren)">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVerify(modelName);
                        }}
                      >
                        <CheckIcon fontSize="small" color="success" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              {hasHistory && (
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Gueltig ab</TableCell>
                          <TableCell>Input/1M</TableCell>
                          <TableCell>Output/1M</TableCell>
                          <TableCell>Typ</TableCell>
                          <TableCell>Verifiziert</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entries.map((entry, i) => (
                          <TableRow key={i} sx={i === 0 ? { fontWeight: 600 } : {}}>
                            <TableCell>{entry.effective_from}</TableCell>
                            <TableCell>${entry.input_price_per_million}</TableCell>
                            <TableCell>${entry.output_price_per_million}</TableCell>
                            <TableCell>{entry.pricing_unit}</TableCell>
                            <TableCell>
                              {entry.last_verified_at
                                ? new Date(entry.last_verified_at + 'Z').toLocaleDateString('de-DE')
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              )}
            </Accordion>
          );
        })
      )}

      {/* Add Price Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuer Modellpreis</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Modellname"
            value={formModelName}
            onChange={(e) => setFormModelName(e.target.value)}
            margin="normal"
            placeholder="z.B. gemini-2.5-flash"
            helperText={
              knownModels.length > 0
                ? `Bekannte Modelle: ${knownModels.slice(0, 5).join(', ')}...`
                : undefined
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Preismodell</InputLabel>
            <Select
              value={formPricingUnit}
              label="Preismodell"
              onChange={(e) => setFormPricingUnit(e.target.value)}
            >
              <MenuItem value="per_million_tokens">Pro 1M Tokens</MenuItem>
              <MenuItem value="per_response">Pro Aufruf</MenuItem>
            </Select>
          </FormControl>
          {formPricingUnit === 'per_million_tokens' ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Input-Preis / 1M Tokens (USD)"
                type="number"
                value={formInputPrice}
                onChange={(e) => setFormInputPrice(e.target.value)}
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
              />
              <TextField
                fullWidth
                label="Output-Preis / 1M Tokens (USD)"
                type="number"
                value={formOutputPrice}
                onChange={(e) => setFormOutputPrice(e.target.value)}
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Box>
          ) : (
            <TextField
              fullWidth
              label="Preis pro Aufruf (USD)"
              type="number"
              value={formPricePerResponse}
              onChange={(e) => setFormPricePerResponse(e.target.value)}
              margin="normal"
              inputProps={{ step: 0.001, min: 0 }}
            />
          )}
          <TextField
            fullWidth
            label="Gueltig ab"
            type="date"
            value={formEffectiveFrom}
            onChange={(e) => setFormEffectiveFrom(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            helperText="Aeltere Token werden mit dem vorherigen Preis berechnet"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleAddPrice}
            disabled={!formModelName}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingSection;
